import { createListenerMiddleware, ListenerMiddlewareInstance, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { io, Socket } from 'socket.io-client';

import { getTokenCookie } from '@shared/lib';
import { BillingPaymentMethodDocument, PromoRollSynthJobsDocument } from 'domain/api/graphql/generated';

import { ZVUK_REALTIME_BFF_URL } from 'application/consts';
import { createLogger } from 'application/utils/LogUtils';

import { setAppInitialized } from 'application/store';
import { fetchCurrentUserData } from '../accountSlice';

const logger = createLogger('RealtimeListener', '#4ecdc4');

type AnyThunkDispatch = ThunkDispatch<any, any, UnknownAction>;

type CreateRealtimeListenerType = (
  apolloClient: ApolloClient<NormalizedCacheObject>
) => ListenerMiddlewareInstance<unknown, ThunkDispatch<unknown, unknown, UnknownAction>, unknown>;

let activeSocket: Socket | null = null;
let activeSocketToken: string | null = null;

type SocketEventHandler = (...args: any[]) => void;

export const addSocketEventListener = (event: string, handler: SocketEventHandler): void => {
  activeSocket?.on(event, handler);
};

export const removeSocketEventListener = (event: string, handler: SocketEventHandler): void => {
  activeSocket?.off(event, handler);
};

export const disconnectRealtimeSocket = (): void => {
  activeSocket?.disconnect();
  activeSocket = null;
  activeSocketToken = null;
};

export const createRealtimeListener: CreateRealtimeListenerType = (apolloClient) => {
  const middleware = createListenerMiddleware();

  middleware.startListening({
    actionCreator: setAppInitialized,
    effect: async (_action, { dispatch: listenerDispatch }) => {
      const dispatch = listenerDispatch as AnyThunkDispatch;
      const token = getTokenCookie();

      logger.info('effect triggered, token:', token ? 'present' : 'missing');

      if (!token) {
        return;
      }

      if (activeSocket?.connected && activeSocketToken === token) {
        logger.debug('already connected with the current token, skipping');
        return;
      }

      activeSocket?.disconnect();
      activeSocket = null;
      activeSocketToken = token;

      const bffUrl = ZVUK_REALTIME_BFF_URL ?? '';

      logger.info('connecting to:', `${bffUrl}/realtime/socket.io`);

      const socket = io(bffUrl, {
        path: '/realtime/socket.io',
        auth: { token },
        transports: ['websocket', 'polling'],
        forceNew: true,
      });

      activeSocket = socket;

      socket.on('connect', () => {
        logger.info('connected, id:', socket.id);
      });

      socket.on('disconnect', (reason) => {
        logger.info('disconnected:', reason);
      });

      socket.on('connect_error', (err) => {
        logger.info('connect_error:', err.message);
      });

      socket.on('company:user_removed', (payload) => {
        logger.info('company:user_removed', payload);
        dispatch(fetchCurrentUserData());
      });

      socket.on('company:contract_migration_updated', async () => {
        logger.info('company:contract_migration_updated');
        dispatch(fetchCurrentUserData());
      });

      socket.on('company:user_added', (payload) => {
        logger.info('company:user_added', payload);
        dispatch(fetchCurrentUserData());
      });

      socket.on('billing:card_linked', (payload) => {
        logger.info('billing:card_linked', payload);
        apolloClient.refetchQueries({ include: [BillingPaymentMethodDocument] });
      });

      socket.on('promo_roll_synth:generation_in_progress', async (payload: { jobId: string }) => {
        logger.info('promo_roll_synth:generation_in_progress', payload);
        apolloClient.refetchQueries({ include: [PromoRollSynthJobsDocument] });
      });

      socket.on(
        'promo_roll_synth:generation_completed',
        async (payload: { jobId: string; promoMediafileId: string }) => {
          logger.info('promo_roll_synth:generation_completed', payload);
          apolloClient.refetchQueries({ include: [PromoRollSynthJobsDocument] });
        }
      );

      socket.on('promo_roll_synth:generation_failed', async (payload: { jobId: string; failureMessage: string }) => {
        logger.info('promo_roll_synth:generation_failed', payload);
        apolloClient.refetchQueries({ include: [PromoRollSynthJobsDocument] });
      });
    },
  });

  return middleware;
};
